import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Shared shell for the public Privacy + Terms pages.
 * Matches the warm, calm RequestReview aesthetic — no corporate legal styling.
 */
const LegalShell = ({ title, lastUpdated, children }) => (
  <div className="min-h-screen bg-[#F3F5F7] text-stone-800">
    <div className="fixed inset-0 grain-overlay pointer-events-none" />
    <header className="border-b border-stone-200 bg-white/70 backdrop-blur-sm relative z-10">
      <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="font-heading text-xl text-[#3F617E]">Maris Desk</Link>
        <Link to="/request-review" className="text-[12.5px] text-stone-500 hover:text-stone-900 transition-colors">
          Request a Guest Request Flow Review →
        </Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16 relative z-10">
      <Link to="/request-review" className="inline-flex items-center text-[12.5px] text-stone-500 hover:text-stone-900 mb-6">
        <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
      </Link>
      <h1 className="font-heading text-3xl sm:text-4xl text-stone-900 mb-2">{title}</h1>
      <p className="text-[12.5px] text-stone-400 mb-10">Last updated: {lastUpdated}</p>
      <div className="prose prose-stone max-w-none text-[15px] leading-relaxed space-y-5">
        {children}
      </div>
    </main>

    <footer className="py-10 border-t border-stone-200 bg-white relative z-10">
      <div className="max-w-3xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-stone-500">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 justify-center sm:justify-start">
          <span>© {new Date().getFullYear()} Maris Desk</span>
          <Link to="/privacy" className="hover:text-stone-900">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-stone-900">Terms of Use</Link>
          <a href="mailto:support@marisdesk.com" className="hover:text-stone-900">Contact: support@marisdesk.com</a>
        </div>
      </div>
    </footer>
  </div>
);

export const Privacy = () => (
  <LegalShell title="Privacy Policy" lastUpdated="June 2026">
    <p>
      Maris Desk helps small hotels, villas, and short-term rentals run a calmer guest desk.
      This page explains what we collect when you submit a Guest Request Flow Review and what we do with it.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">What we collect</h2>
    <p>
      When you complete the Guest Request Flow Review form, we collect the information you provide, which may include:
      your name, email, property name, website or booking link, property type, your main concern, and any message or notes.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">How we use it</h2>
    <p>
      We use your information only to review your request, contact you about the review, and understand whether Maris Desk
      may be a fit for your property. We do not sell your information. We do not share it with third parties for marketing.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">How we store it</h2>
    <p>
      Submitted leads are stored securely in our database. Access is limited to Maris Desk team members who need it
      to respond to you. Passwords for any operator accounts are hashed; we never store plaintext credentials.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">Your choices</h2>
    <p>
      You can contact us at <a href="mailto:support@marisdesk.com" className="text-[#3F617E] underline">support@marisdesk.com</a>{' '}
      at any time to ask questions, request corrections, or ask us to delete the information you submitted.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">Cookies</h2>
    <p>
      Maris Desk's public landing page uses only essential cookies needed for the site to function. We do not use
      advertising or third-party tracking cookies. If that ever changes, we'll update this section and add a clear
      notice on the page.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">Contact</h2>
    <p>
      Questions about this policy? Email{' '}
      <a href="mailto:support@marisdesk.com" className="text-[#3F617E] underline">support@marisdesk.com</a>.
    </p>
  </LegalShell>
);

export const Terms = () => (
  <LegalShell title="Terms of Use" lastUpdated="June 2026">
    <p>
      These terms apply when you visit marisdesk.com and use the Guest Request Flow Review form. They're short on purpose —
      we want them clear, not corporate.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">What the review is</h2>
    <p>
      The Guest Request Flow Review is informational. We share observations and suggestions based on the information you
      provide and on publicly available information about your property. It does not guarantee improved reviews, revenue,
      savings, occupancy, or any specific operational outcome.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">What we are not responsible for</h2>
    <p>
      Maris Desk is not responsible for your day-to-day hotel operations, staffing decisions, guest disputes, refunds,
      maintenance choices, vendor performance, or anything that happens on-property. Recommendations are guidance, not
      instructions.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">No client relationship until signed</h2>
    <p>
      Submitting the Guest Request Flow Review form, receiving a review, or exchanging emails with us does not create
      a client relationship. A formal client relationship begins only when a separate written service agreement is signed
      by both sides.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">Using the site fairly</h2>
    <p>
      Please don't submit the form on someone else's behalf without their permission, don't try to break or probe the site,
      and don't submit content that's illegal or abusive. We may decline to respond to submissions that violate these
      basic expectations.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">Changes</h2>
    <p>
      We may update these terms occasionally. The "Last updated" date above will reflect any change. Continuing to use
      the site after a change means you accept the updated terms.
    </p>

    <h2 className="font-heading text-xl text-stone-900 mt-8 mb-2">Contact</h2>
    <p>
      Questions about these terms? Email{' '}
      <a href="mailto:support@marisdesk.com" className="text-[#3F617E] underline">support@marisdesk.com</a>.
    </p>
  </LegalShell>
);

export default Privacy;
